<div class="new-rule-page">
  <ui-page-header
    [title]="(this.mode === 'edit' ? 'workflows.builder.title_edit' : 'workflows.new_rule.title') | translate"
    [breadcrumbs]="breadcrumbs"
    [showBackButton]="true"
    [titleTag]="activeStep === 1 ? statusTagLabel : ''"
    [titleTagType]="$any(statusTagType)"
  >
    <ng-container *ngIf="activeStep === 1">
      <ui-button
        type="transparent"
        [label]="'common.cancel' | translate"
        (click)="cancel()"
      ></ui-button>

      <ng-container *ngIf="mode === 'create'; else editActions">
        <ui-button
          type="secondary"
          [label]="'workflows.builder.save_draft' | translate"
          [disabled]="!builder?.canSave || !!(saving$ | async)"
          (click)="triggerSave('draft')"
        ></ui-button>
        <ui-button
          type="primary"
          icon="bolt"
          iconSize="small"
          [label]="'workflows.builder.save_activate' | translate"
          [disabled]="!builder?.canSave || !!(saving$ | async)"
          (click)="triggerSave('active')"
        ></ui-button>
      </ng-container>

      <ng-template #editActions>
        <ui-button
          type="secondary"
          [label]="(builder?.secondaryEditLabelKey || 'common.activate') | translate"
          [disabled]="!builder?.canSave || !!(saving$ | async)"
          (click)="triggerSave(builder!.secondaryEditTargetStatus)"
        ></ui-button>
        <ui-button
          type="primary"
          [label]="'common.save' | translate"
          [disabled]="!builder?.canSave || !!(saving$ | async)"
          (click)="triggerSave(builder!.currentStatus)"
        ></ui-button>
      </ng-template>
    </ng-container>
  </ui-page-header>

  <div class="new-rule-page__content" [class.new-rule-page__content--builder]="activeStep === 1">
    <!-- ── Loading (edit mode, while the rule + schema arrive) ──────────── -->
    <div class="new-rule-page__loading" *ngIf="isLoading">
      <mat-spinner [diameter]="40"></mat-spinner>
      <p class="new-rule-page__loading-text">{{ 'workflows.new_rule.loading_rule' | translate }}</p>
    </div>

    <!-- ── Step 0: Entry ──────────────────────────────────────────────── -->
    <ng-container *ngIf="activeStep === 0 && !isLoading">
      <div class="new-rule-page__entry">
        <div class="new-rule-page__hero">
          <ui-svg-icon class="new-rule-page__hero-icon" svgIcon="bolt" size="large"></ui-svg-icon>
          <h1 class="new-rule-page__hero-title">{{ 'workflows.new_rule.page_title' | translate }}</h1>
          <p class="new-rule-page__hero-subtitle">{{ 'workflows.new_rule.page_subtitle' | translate }}</p>
        </div>

        <section class="new-rule-page__ai-card" aria-labelledby="new-rule-ai-heading">
          <h2 id="new-rule-ai-heading" class="new-rule-page__ai-heading">
            <ui-svg-icon svgIcon="ai-volta-icon-cc" size="medium"></ui-svg-icon>
            {{ 'workflows.new_rule.ai.heading' | translate }}
          </h2>
          <textarea
            class="new-rule-page__ai-textarea"
            rows="4"
            [formControl]="aiDescriptionControl"
            [placeholder]="'workflows.new_rule.ai.placeholder' | translate"
          ></textarea>
          <p class="new-rule-page__ai-note">{{ 'workflows.new_rule.ai.note' | translate }}</p>
          <ui-button
            type="primary"
            [label]="aiButtonLabel"
            icon="ai-volta-icon-cc"
            iconSize="large"
            [isLoading]="aiGenerating"
            [disabled]="aiGenerating"
            (click)="generateWithAi()"
          ></ui-button>
        </section>

        <div class="new-rule-page__divider">
          <span>{{ 'workflows.new_rule.or_another_way' | translate }}</span>
        </div>

        <button type="button" class="new-rule-page__scratch" (click)="buildManually()">
          <ui-svg-icon svgIcon="rench" size="medium"></ui-svg-icon>
          <span class="new-rule-page__scratch-text">
            <span class="new-rule-page__scratch-title">{{ 'workflows.new_rule.scratch.title' | translate }}</span>
            <span class="new-rule-page__scratch-desc">{{ 'workflows.new_rule.scratch.desc' | translate }}</span>
          </span>
          <ui-svg-icon class="new-rule-page__scratch-arrow" svgIcon="arrow-right" size="medium"></ui-svg-icon>
        </button>

        <!-- Backend-owned templates. Absent when the catalog is empty or failed to
             load — "from scratch" above always remains available, so the divider
             lives inside the guard rather than dangling over nothing. -->
        <ng-container *ngIf="templates$ | async as templates">
          <ng-container *ngIf="templates.length">
            <div class="new-rule-page__divider">
              <span id="new-rule-templates-heading">
                {{ 'workflows.new_rule.templates.heading' | translate }}
              </span>
            </div>

            <section class="new-rule-page__templates" aria-labelledby="new-rule-templates-heading">
              <button
                type="button"
                class="new-rule-page__template-card"
                *ngFor="let template of templates; trackBy: trackByTemplateKey"
                (click)="startFromTemplate(template)"
              >
                <ui-svg-icon class="new-rule-page__template-icon" svgIcon="bolt" size="medium"></ui-svg-icon>
                <span class="new-rule-page__template-text">
                  <span class="new-rule-page__template-title">
                    {{ template.i18nLabel | translate }}
                    <!-- Says up front that the template's steps are fixed, so the locked
                         rows in the builder are expected rather than a surprise. -->
                    <span
                      class="new-rule-page__template-lock"
                      [matTooltip]="'workflows.new_rule.templates.locked_tooltip' | translate"
                      matTooltipPosition="above"
                    >
                      <ui-svg-icon svgIcon="lock" size="small"></ui-svg-icon>
                    </span>
                  </span>
                  <span class="new-rule-page__template-desc">{{ template.i18nDescription | translate }}</span>
                </span>
                <ui-svg-icon class="new-rule-page__template-arrow" svgIcon="arrow-right" size="medium"></ui-svg-icon>
              </button>
            </section>
          </ng-container>
        </ng-container>
      </div>
    </ng-container>

    <!-- ── Step 1: Builder ────────────────────────────────────────────── -->
    <app-workflow-builder-step
      *ngIf="activeStep === 1 && schema"
      [rule]="rule"
      [schema]="schema"
      [origin]="origin"
      (ruleChange)="onRuleChange($any($event))"
      (save)="onBuilderSave($any($event))"
    ></app-workflow-builder-step>
  </div>
</div>
